// src/modules/login.js — pantalla de ingreso.
//
// FASE 4 · F1.2 (creacion). Refactores:
//   - Bloque 4 (24-may): renderLogin pasa a pantalla intermedia con intent.
//   - Bloque 6 (24-may): 3 cambios acoplados:
//       6.1 — Visual: el form vive en 2 cajas alternativas dentro del glass-card
//             (Google vs email+clave), separadas por divider "O". Clases
//             .auth-option-box (bg rgba(0,0,0,.12) para evitar muñecas rusas)
//             y .auth-option-divider en styles.css.
//       6.2 — Modo register eliminado. La UI tiene un solo boton "Ingresar".
//             Pre-check con check_email_provider (RPC Bloque 3B) decide:
//               * email es de Google -> mensaje y NO toca submit.
//               * email existe con provider email -> intentar login.
//                   * Clave incorrecta primer intento -> mensaje + foco al
//                     password (contador en sessionStorage por email).
//                   * Clave incorrecta segundo intento -> mensaje + reset
//                     automatico (requestPasswordReset).
//                 Contador se resetea cuando el email tipeado cambia.
//               * email no existe -> signUp automatico (sin pedir mas datos);
//                 el trigger handle_new_auth_user (schema §23, Bloque 3C) crea
//                 la fila en usuarios con global_nickname = email completo.
//       6.3 — (vive en main.js renderIngreso, no aca).
//
// auth.js NO se toca (PKCE sensible, alerta del bloque): signUpWithEmail se
// sigue llamando con 2 args (email, password); los otros 2 quedan undefined.

import { appRoot } from '../utils/dom.js'
import { supabase } from '../supabase.js'
import { renderBrandLogo } from '../ui/brand-logo.js'
import { queryWithTimeout, withTimeout, NET_TIMEOUT_MS } from '../utils/timeout.js'
import {
  signInWithGoogle,
  signInWithEmail,
  signUpWithEmail,
  requestPasswordReset,
  getCurrentUser,
} from '../core/auth.js'

const GOOGLE_LOGO = 'https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg'
const MIN_PASSWORD = 6

// Bloque 4: persistir el intent antes del redirect a Google. Consumer aparece
// en Bloque 9 (carril del invitado). sessionStorage (no localStorage) -> muere
// con la pestana.
const LOGIN_INTENT_KEY = 'pacto:loginIntent'

// Bloque 8 (pulir flash): flag de "join in progress" para el carril del
// invitado. Se setea ANTES de signUp/signIn/Google y main.js lo lee en
// handleGuestRoute para pintar loader "Sumandote..." en vez de
// renderInvitado durante la ventana SIGNED_IN -> joinGroup OK.
// String duplicado en main.js (mantener sincronizado).
const JOIN_IN_PROGRESS_KEY = 'pacto:joining'
const setJoining = (codigo) => {
  try { sessionStorage.setItem(JOIN_IN_PROGRESS_KEY, codigo) } catch (_) { /* ignore */ }
}

// Bloque 6.2: contador de errores de clave por email tipeado. 1er fallo ->
// mensaje "probá de nuevo"; 2do fallo -> reset automatico. El contador se
// resetea al cambiar el email tipeado o al loguearse OK.
const AUTH_FAILS_KEY = 'pacto:authFails'

const getFailsForEmail = (email) => {
  try {
    const raw = sessionStorage.getItem(AUTH_FAILS_KEY)
    if (!raw) return 0
    const o = JSON.parse(raw)
    return o && o.email === email ? Number(o.count) || 0 : 0
  } catch (_) { return 0 }
}
const incFailsForEmail = (email) => {
  const next = getFailsForEmail(email) + 1
  try {
    sessionStorage.setItem(AUTH_FAILS_KEY, JSON.stringify({ email, count: next }))
  } catch (_) { /* ignore */ }
  return next
}
const clearFails = () => {
  try { sessionStorage.removeItem(AUTH_FAILS_KEY) } catch (_) { /* ignore */ }
}

// SVGs inline para el toggle mostrar/ocultar de la clave futbolera.
const EYE_OPEN_SVG = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`
const EYE_OFF_SVG  = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`

// Bloque 6.2: pre-check del provider del email via RPC publica (Bloque 3B).
// Devuelve { exists: boolean, provider: 'google'|'email'|null, error? }.
const checkEmailProvider = async (email) => {
  // DT-H3-resto: timeout. En timeout/error → {error}; el handler muestra el
  // mensaje y rehabilita el botón (= reintentar), no cuelga "Procesando…".
  let data, error
  try {
    // Doble cinturón: queryWithTimeout aborta el fetch, pero NO interrumpe un
    // hang PRE-fetch en el lock de GoTrue (al armar el header Authorization la
    // RPC pide la sesión → _acquireLock; si está tomado, cuelga antes de fetch y
    // el abort no sirve). withTimeout es un Promise.race con timer duro que SÍ
    // rechaza aunque el await interno nunca resuelva. +1s para que el camino
    // normal (abort del fetch) gane cuando el cuelgue es de red.
    ({ data, error } = await withTimeout(
      queryWithTimeout((s) => supabase.rpc('check_email_provider', { p_email: email }).abortSignal(s), NET_TIMEOUT_MS, 'check_email_provider'),
      NET_TIMEOUT_MS + 1000, 'check_email_provider'))
  } catch (err) {
    return { error: { message: 'timeout' }, exists: false, provider: null }
  }
  if (error) return { error, exists: false, provider: null }
  const row = (data || [])[0]
  return { error: null, exists: Boolean(row?.exists_in_db), provider: row?.provider || null }
}

// Traduce los errores crudos de Supabase Auth (ingles) a mensajes en espanol.
// CLAUDE.md §4: no se asume estructura — se lee error.message y se degrada a
// un mensaje generico si no matchea ningun caso conocido.
const traducirError = (error) => {
  const msg = (error?.message || '').toLowerCase()
  if (msg.includes('invalid login credentials')) return 'Correo o clave futbolera incorrectos.'
  if (msg.includes('already registered') || msg.includes('already been registered')) {
    return 'Ese email ya esta registrado. Inicia sesion o recupera tu clave.'
  }
  if (msg.includes('email not confirmed')) return 'Confirma tu correo antes de ingresar.'
  if (msg.includes('password should be at least')) {
    return `La clave futbolera necesita al menos ${MIN_PASSWORD} caracteres.`
  }
  if (msg.includes('unable to validate email') || msg.includes('invalid email')) {
    return 'Revisa el correo: no parece valido.'
  }
  if (msg.includes('rate limit') || msg.includes('too many')) {
    return 'Demasiados intentos. Espera un momento y prueba de nuevo.'
  }
  return error?.message || 'No pudimos completar la operacion. Proba de nuevo.'
}

// applyIntent — traduce el intent a navegacion (hash). main.js + router se
// encargan del resto (renderApp post-onAuthStateChange + router por hash).
const applyIntent = (intent /* payload reservado para Bloque 9 */) => {
  if (intent === 'crear')      { window.location.hash = '/crear-grupo'; return }
  if (intent === 'unirse')     { window.location.hash = '/ingreso';     return }
  if (intent === 'mis-grupos') { window.location.hash = '/mis-grupos';  return }
  // Cualquier otro intent: raiz (router resuelve auto-grupo o Pantalla 1).
  window.location.hash = '/'
}

export const renderLogin = (intent = 'mis-grupos', payload = null) => {
  appRoot.innerHTML = `
    <div class="glass-card view-enter" style="text-align:center;">
      ${renderBrandLogo()}
      <p class="subtitle mb-6">Inicia sesion para entrar a la cancha.</p>

      <div class="auth-option-box">
        <button class="btn btn-google" id="btnGoogle" type="button" style="margin-bottom:0;">
          <img src="${GOOGLE_LOGO}" width="20" alt="">
          Continuar con Google
        </button>
      </div>

      <div class="auth-option-divider"><span>O</span></div>

      <div class="auth-option-box">
        <div class="input-group" style="text-align:left;">
          <label>Correo</label>
          <input type="email" id="loginEmail" placeholder="nico@ejemplo.com" autocomplete="email">
        </div>
        <div class="input-group" style="text-align:left;">
          <label>Clave futbolera</label>
          <div class="input-with-eye">
            <input type="password" id="loginPassword" placeholder="Minimo ${MIN_PASSWORD} caracteres" autocomplete="current-password">
            <button type="button" id="btnTogglePass" class="btn-eye" aria-label="Mostrar clave">${EYE_OPEN_SVG}</button>
          </div>
        </div>
        <button class="btn btn-primary" id="btnSubmit" type="button" style="margin-bottom:0;">Ingresar</button>
      </div>

      <div id="loginError" class="error-msg"></div>
    </div>
  `

  const errorBox = document.getElementById('loginError')
  const showError = (text) => { errorBox.textContent = text; errorBox.classList.add('visible') }
  const clearError = () => { errorBox.textContent = ''; errorBox.classList.remove('visible') }

  const btnSubmit = document.getElementById('btnSubmit')
  const setBusy = (busy) => {
    btnSubmit.disabled = busy
    btnSubmit.textContent = busy ? 'Procesando...' : 'Ingresar'
    btnSubmit.style.opacity = busy ? '0.6' : ''
  }

  // Bloque 6: ojito mostrar/ocultar clave.
  const inputPass = document.getElementById('loginPassword')
  const btnTogglePass = document.getElementById('btnTogglePass')
  btnTogglePass?.addEventListener('click', () => {
    const hidden = inputPass.type === 'password'
    inputPass.type = hidden ? 'text' : 'password'
    btnTogglePass.innerHTML = hidden ? EYE_OFF_SVG : EYE_OPEN_SVG
    btnTogglePass.setAttribute('aria-label', hidden ? 'Ocultar clave' : 'Mostrar clave')
    inputPass.focus()
  })

  // Bloque 6.2: el contador de fails se resetea cuando el email tipeado cambia.
  document.getElementById('loginEmail')?.addEventListener('input', () => clearFails())

  document.getElementById('btnGoogle').addEventListener('click', async () => {
    clearError()
    // Persistir intent para que sobreviva al redirect OAuth. Consumer aparece
    // en Bloque 9 (carril del invitado); aca queda escrito sin lector.
    try {
      sessionStorage.setItem(LOGIN_INTENT_KEY, JSON.stringify({ intent, payload }))
    } catch (_) { /* sessionStorage puede no estar disponible — no bloquea el flujo */ }
    const { error } = await signInWithGoogle()
    // Sin error: el navegador ya esta redirigiendo a Google.
    if (error) showError(traducirError(error))
  })

  btnSubmit.addEventListener('click', async () => {
    clearError()
    const email = document.getElementById('loginEmail').value.trim()
    const password = document.getElementById('loginPassword').value
    if (!email) { showError('Ingresa tu correo.'); return }
    if (password.length < MIN_PASSWORD) {
      showError(`La clave futbolera necesita al menos ${MIN_PASSWORD} caracteres.`)
      return
    }
    setBusy(true)

    // 1) Pre-check del provider del email (Bloque 3B).
    const pre = await checkEmailProvider(email)
    if (pre.error) {
      setBusy(false)
      showError('No pudimos verificar el correo. Proba de nuevo.')
      return
    }

    // 2) Email es de Google -> redirigir al boton de arriba.
    if (pre.exists && pre.provider === 'google') {
      setBusy(false)
      showError('Esa cuenta es de Google. Toca "Continuar con Google" arriba.')
      return
    }

    // 3) Email existe con provider=email -> intentar login.
    if (pre.exists) {
      const { error } = await signInWithEmail(email, password)
      if (!error) {
        clearFails()
        applyIntent(intent, payload)
        return
      }
      const msg = (error.message || '').toLowerCase()
      if (msg.includes('invalid login credentials')) {
        // Clave incorrecta: contador por email.
        const fails = incFailsForEmail(email)
        if (fails >= 2) {
          // 2do intento o mas -> disparar reset automatico.
          const r = await requestPasswordReset(email)
          setBusy(false)
          clearFails()
          if (r.error) {
            showError('Clave incorrecta otra vez. Hubo un problema enviando el mail de reseteo; probá en un rato.')
          } else {
            showError('Clave incorrecta otra vez. Te enviamos un mail para resetear la clave. Revisá tu correo (y spam).')
          }
          return
        }
        // 1er intento -> solo mensaje + foco al password.
        setBusy(false)
        showError('Clave incorrecta, probá de nuevo.')
        inputPass?.focus()
        try { inputPass?.select?.() } catch (_) { /* algunos browsers no soportan select en type=password */ }
        return
      }
      setBusy(false)
      showError(traducirError(error))
      return
    }

    // 4) Email NO existe -> signUp automatico (sin pedir mas datos).
    //    auth.js NO se toca: signUpWithEmail acepta 2 args; los otros 2 quedan
    //    undefined y el codigo existente los maneja sin efecto. El trigger
    //    handle_new_auth_user crea la fila en public.usuarios con
    //    global_nickname = email completo (parche Bloque 3C).
    const { data, error } = await signUpWithEmail(email, password)
    if (error) {
      setBusy(false)
      showError(traducirError(error))
      return
    }
    if (!data?.session) {
      // Confirm Email = OFF en DEV (verificado Bloque 4) -> esta rama es
      // defensiva por si en algun momento se prende.
      setBusy(false)
      showError('Te enviamos un correo para confirmar tu cuenta.')
      return
    }
    clearFails()
    applyIntent(intent, payload)
  })
}

// Bloque 6.5: pantalla de reseteo de clave disparada por PASSWORD_RECOVERY.
// Llamada desde main.js cuando llega el evento o cuando el bootstrap detecta
// `type=recovery` en el hash inicial. Recibe onDone() para volver al flow
// normal cuando el reset se completa con exito.
//
// Reusa: glass-card, .auth-option-box, .input-with-eye, EYE_OPEN_SVG/OFF_SVG,
// MIN_PASSWORD, traducirError, supabase. NO toca auth.js.

export const renderResetPassword = (onDone) => {
  appRoot.innerHTML = `
    <div class="glass-card view-enter" style="text-align:center;">
      ${renderBrandLogo()}
      <p class="subtitle mb-6">Elegí tu nueva clave futbolera.</p>

      <div class="auth-option-box">
        <div class="input-group" style="text-align:left;">
          <label>Nueva clave</label>
          <div class="input-with-eye">
            <input type="password" id="resetPass1" placeholder="Mínimo ${MIN_PASSWORD} caracteres" autocomplete="new-password">
            <button type="button" id="btnEye1" class="btn-eye" aria-label="Mostrar clave">${EYE_OPEN_SVG}</button>
          </div>
        </div>
        <div class="input-group" style="text-align:left;">
          <label>Confirmá tu clave</label>
          <div class="input-with-eye">
            <input type="password" id="resetPass2" placeholder="Repetí la clave" autocomplete="new-password">
            <button type="button" id="btnEye2" class="btn-eye" aria-label="Mostrar clave">${EYE_OPEN_SVG}</button>
          </div>
        </div>
        <button class="btn btn-primary" id="btnGuardarClave" type="button" style="margin-bottom:0;">Guardar clave</button>
      </div>

      <div id="resetError" class="error-msg"></div>
    </div>
  `

  const errorBox = document.getElementById('resetError')
  const showError = (text) => { errorBox.textContent = text; errorBox.classList.add('visible') }
  const clearError = () => { errorBox.textContent = ''; errorBox.classList.remove('visible') }

  // Ojito independiente por input — toggle del .type entre password y text.
  const wireEye = (btnId, inputId) => {
    const btn = document.getElementById(btnId)
    const input = document.getElementById(inputId)
    btn?.addEventListener('click', () => {
      const hidden = input.type === 'password'
      input.type = hidden ? 'text' : 'password'
      btn.innerHTML = hidden ? EYE_OFF_SVG : EYE_OPEN_SVG
      btn.setAttribute('aria-label', hidden ? 'Ocultar clave' : 'Mostrar clave')
      input.focus()
    })
  }
  wireEye('btnEye1', 'resetPass1')
  wireEye('btnEye2', 'resetPass2')

  const btnGuardar = document.getElementById('btnGuardarClave')
  const setBusy = (busy) => {
    btnGuardar.disabled = busy
    btnGuardar.textContent = busy ? 'Guardando...' : 'Guardar clave'
    btnGuardar.style.opacity = busy ? '0.6' : ''
  }

  btnGuardar.addEventListener('click', async () => {
    clearError()
    const p1 = document.getElementById('resetPass1').value
    const p2 = document.getElementById('resetPass2').value
    if (p1.length < MIN_PASSWORD) {
      showError(`La clave necesita al menos ${MIN_PASSWORD} caracteres.`)
      return
    }
    if (p1 !== p2) {
      showError('Las claves no coinciden.')
      return
    }
    setBusy(true)
    const { error } = await supabase.auth.updateUser({ password: p1 })
    if (error) {
      setBusy(false)
      showError(traducirError(error))
      return
    }
    // Exito: salir del modo recovery y volver al flow normal (lo maneja main.js).
    onDone?.()
  })
}

// ─── Bloque 8 — Carril del invitado (v2.1 §3) ────────────────────────────
//
// renderInvitado(codigo, preview, onJoin) — pantalla del usuario que llega
// por link pactomundial.futbol/g/CODIGO. Detecta modo segun sesion:
//   - Modo "logueado" (user con sesion, no miembro del grupo): header con
//     nombre del grupo + input apodo + boton Sumarme.
//   - Modo "invitado" (sin sesion): header + 2 cajas auth (Google /
//     email+clave) + input apodo + boton Sumarme. Tras login OK, dispara
//     joinGroup automaticamente.
//
// Params:
//   codigo   — el codigo del grupo (UPPER).
//   preview  — { nombre, codigo, jugadores_count, status } del getGroupPreview.
//   onJoin   — async (codigo, apodo) => { data, error }. Inyectado por main.js
//              (envuelve joinGroup + setActiveGroupCode + navigate al home).
//
// Reusa helpers privados de este modulo: checkEmailProvider, traducirError,
// isAlreadyRegistered, getFailsForEmail/incFailsForEmail/clearFails,
// MIN_PASSWORD, EYE_OPEN_SVG/OFF_SVG, GOOGLE_LOGO, LOGIN_INTENT_KEY,
// getCurrentUser (importado arriba).

const renderInvitadoLogueado = (codigo, preview, onJoin, initialErrorMsg = null) => {
  appRoot.innerHTML = `
    <div class="glass-card view-enter">
      <p class="subtitle mb-2" style="text-align:center; font-size:.78rem; letter-spacing:.06em;">SUMATE A</p>
      <h2 class="mb-2" style="font-family: var(--font-heading); text-align:center;">${preview.nombre}</h2>
      <p class="subtitle mb-4" style="text-align:center;">Código: ${preview.codigo}</p>

      <div class="input-group mb-4">
        <label>Apodo en este grupo</label>
        <input type="text" id="apodoInvitado" maxlength="20" placeholder="Tu apodo aquí (máx 20)">
      </div>

      <div id="invitadoError" class="error-msg"></div>
      <button class="btn btn-primary" id="btnSumarmeInvitado">Sumarme</button>
      <button class="btn btn-secondary" id="btnInvitadoCancelar">Volver al inicio</button>
    </div>
  `
  wireInvitadoSubmit(codigo, preview, onJoin)
  document.getElementById('btnInvitadoCancelar')?.addEventListener('click', () => { window.location.hash = '/' })
  paintInitialError(initialErrorMsg)
}

const renderInvitadoSinSesion = (codigo, preview, onJoin, initialErrorMsg = null) => {
  appRoot.innerHTML = `
    <div class="glass-card view-enter">
      <p class="subtitle mb-2" style="text-align:center; font-size:.78rem; letter-spacing:.06em;">SUMATE A</p>
      <h2 class="mb-2" style="font-family: var(--font-heading); text-align:center;">${preview.nombre}</h2>
      <p class="subtitle mb-4" style="text-align:center;">Código: ${preview.codigo}</p>

      <div class="auth-option-box">
        <button class="btn btn-google" id="btnGoogleInvitado" type="button" style="margin-bottom:0;">
          <img src="${GOOGLE_LOGO}" width="20" alt="">
          Continuar con Google
        </button>
      </div>

      <div class="auth-option-divider"><span>O</span></div>

      <div class="auth-option-box">
        <div class="input-group" style="text-align:left;">
          <label>Correo</label>
          <input type="email" id="invitadoEmail" placeholder="nico@ejemplo.com" autocomplete="email">
        </div>
        <div class="input-group" style="text-align:left; margin-bottom:0;">
          <label>Clave futbolera</label>
          <div class="input-with-eye">
            <input type="password" id="invitadoPassword" placeholder="Mínimo ${MIN_PASSWORD} caracteres" autocomplete="current-password">
            <button type="button" id="btnEyeInvitado" class="btn-eye" aria-label="Mostrar clave">${EYE_OPEN_SVG}</button>
          </div>
        </div>
      </div>

      <div class="input-group" style="margin-top:1rem;">
        <label>Apodo en este grupo</label>
        <input type="text" id="apodoInvitado" maxlength="20" placeholder="Tu apodo aquí (máx 20)">
      </div>

      <div id="invitadoError" class="error-msg"></div>
      <button class="btn btn-primary" id="btnSumarmeInvitado">Sumarme</button>
      <button class="btn btn-secondary" id="btnInvitadoCancelar">Volver al inicio</button>
    </div>
  `

  // Ojito.
  const inputPass = document.getElementById('invitadoPassword')
  const btnEye = document.getElementById('btnEyeInvitado')
  btnEye?.addEventListener('click', () => {
    const hidden = inputPass.type === 'password'
    inputPass.type = hidden ? 'text' : 'password'
    btnEye.innerHTML = hidden ? EYE_OFF_SVG : EYE_OPEN_SVG
    btnEye.setAttribute('aria-label', hidden ? 'Ocultar clave' : 'Mostrar clave')
    inputPass.focus()
  })

  // Reset contador de fails al cambiar el email tipeado.
  document.getElementById('invitadoEmail')?.addEventListener('input', () => clearFails())

  // Google: capturar apodo, persistir intent + redirigir.
  document.getElementById('btnGoogleInvitado')?.addEventListener('click', async () => {
    const errorBox = document.getElementById('invitadoError')
    errorBox.classList.remove('visible')
    const apodo = document.getElementById('apodoInvitado').value.trim()
    if (!apodo) {
      errorBox.textContent = 'Completá tu apodo antes de continuar.'
      errorBox.classList.add('visible')
      document.getElementById('apodoInvitado')?.focus()
      return
    }
    if (apodo.length > 20 || apodo.includes('@')) {
      errorBox.textContent = apodo.length > 20 ? 'El apodo no puede tener más de 20 caracteres.' : 'El apodo no puede ser un email. Elegí un alias corto.'
      errorBox.classList.add('visible')
      document.getElementById('apodoInvitado')?.focus()
      return
    }
    try {
      sessionStorage.setItem(LOGIN_INTENT_KEY, JSON.stringify({ intent: 'invitado', codigo, apodo }))
    } catch (_) { /* sessionStorage no disponible — joinGroup post-OAuth no auto-disparara */ }
    setJoining(codigo)
    const { error } = await signInWithGoogle()
    if (error) {
      errorBox.textContent = traducirError(error)
      errorBox.classList.add('visible')
    }
    // Sin error: el navegador ya esta redirigiendo a Google.
  })

  document.getElementById('btnInvitadoCancelar')?.addEventListener('click', () => { window.location.hash = '/' })

  wireInvitadoSubmit(codigo, preview, onJoin)
  paintInitialError(initialErrorMsg)
}

// Wiring comun del boton "Sumarme" (email+clave en modo invitado, o solo
// apodo en modo logueado). Diferencia el modo leyendo si existe el input
// de email — si no existe, es modo logueado (sin auth).
const wireInvitadoSubmit = (codigo, preview, onJoin) => {
  const btn = document.getElementById('btnSumarmeInvitado')
  const errorBox = document.getElementById('invitadoError')
  const showError = (t) => { errorBox.textContent = t; errorBox.classList.add('visible') }
  const clearError = () => { errorBox.textContent = ''; errorBox.classList.remove('visible') }
  const setBusy = (busy) => {
    btn.disabled = busy
    btn.textContent = busy ? 'Procesando...' : 'Sumarme'
    btn.style.opacity = busy ? '0.6' : ''
  }

  btn?.addEventListener('click', async () => {
    clearError()
    const apodo = document.getElementById('apodoInvitado').value.trim()
    if (!apodo) {
      showError('Completá tu apodo en el grupo.')
      document.getElementById('apodoInvitado')?.focus()
      return
    }
    if (apodo.length > 20) {
      showError('El apodo no puede tener más de 20 caracteres.')
      document.getElementById('apodoInvitado')?.focus()
      return
    }
    if (apodo.includes('@')) {
      showError('El apodo no puede ser un email. Elegí un alias corto.')
      document.getElementById('apodoInvitado')?.focus()
      return
    }

    const emailInput = document.getElementById('invitadoEmail')
    const isLoggedMode = !emailInput

    setBusy(true)

    if (isLoggedMode) {
      // Modo logueado: directo al joinGroup. Setear flag para que un
      // re-render de main.js (no esperado, pero defensivo) pinte loader.
      setJoining(codigo)
      const { error } = await onJoin(codigo, apodo)
      if (error) { setBusy(false); handleJoinError(error, showError); return }
      // onJoin OK navega al home; no necesitamos hacer mas.
      return
    }

    // Modo invitado: pre-check email + login/signUp + joinGroup.
    const email = emailInput.value.trim()
    const password = document.getElementById('invitadoPassword').value
    if (!email) { setBusy(false); showError('Ingresá tu correo.'); return }
    if (password.length < MIN_PASSWORD) {
      setBusy(false)
      showError(`La clave futbolera necesita al menos ${MIN_PASSWORD} caracteres.`)
      return
    }

    // 1) Pre-check provider.
    const pre = await checkEmailProvider(email)
    if (pre.error) { setBusy(false); showError('No pudimos verificar el correo. Probá de nuevo.'); return }
    if (pre.exists && pre.provider === 'google') {
      setBusy(false)
      showError('Esa cuenta es de Google. Tocá "Continuar con Google" arriba.')
      return
    }

    // 2) Email existe -> intentar login (sin auto-reset acá; el reset
    //    automatico vive en renderLogin, no en renderInvitado, para no
    //    cortarle el flujo al usuario que quiere sumarse al grupo).
    if (pre.exists) {
      // Setear flag ANTES del signIn: el SIGNED_IN dispara renderApp y
      // queremos que pinte loader en vez de renderInvitado.
      setJoining(codigo)
      const { error } = await signInWithEmail(email, password)
      if (error) {
        const msg = (error.message || '').toLowerCase()
        if (msg.includes('invalid login credentials')) {
          const fails = incFailsForEmail(email)
          if (fails >= 2) {
            const r = await requestPasswordReset(email)
            setBusy(false); clearFails()
            showError(r.error
              ? 'Clave incorrecta otra vez. Hubo un problema enviando el mail de reseteo; probá en un rato.'
              : 'Clave incorrecta otra vez. Te enviamos un mail para resetear la clave. Revisá tu correo (y spam).')
            return
          }
          setBusy(false)
          showError('Clave incorrecta, probá de nuevo.')
          document.getElementById('invitadoPassword')?.focus()
          return
        }
        setBusy(false); showError(traducirError(error)); return
      }
      // Login OK -> joinGroup directo.
      clearFails()
      const { error: joinErr } = await onJoin(codigo, apodo)
      if (joinErr) { setBusy(false); handleJoinError(joinErr, showError); return }
      return
    }

    // 3) Email NO existe -> auto-signUp + joinGroup.
    // Setear flag ANTES del signUp: dispara SIGNED_IN y main.js debe pintar
    // loader en vez de flash de renderInvitado modo logueado.
    setJoining(codigo)
    const { data, error: signErr } = await signUpWithEmail(email, password)
    if (signErr) { setBusy(false); showError(traducirError(signErr)); return }
    if (!data?.session) {
      setBusy(false)
      showError('Te enviamos un correo para confirmar tu cuenta antes de sumarte al grupo.')
      return
    }
    clearFails()
    const { error: joinErr2 } = await onJoin(codigo, apodo)
    if (joinErr2) { setBusy(false); handleJoinError(joinErr2, showError); return }
  })
}

const handleJoinError = (error, showError) => {
  const m = (error.message || '').toLowerCase()
  if (m.includes('apodo_ocupado')) {
    showError('Ese apodo ya está usado en este grupo, elegí otro.')
    document.getElementById('apodoInvitado')?.focus()
    return
  }
  if (m.includes('ya_sos_miembro')) {
    // No deberia llegar aca (handleGuestRoute en main.js intercepta antes
    // de pintar renderInvitado), pero defensivo: el caller navegara.
    showError('Ya sos parte de este grupo.')
    return
  }
  if (m.includes('limite_grupos')) { showError('Llegaste al límite de grupos por usuario.'); return }
  if (m.includes('grupo_cerrado'))  { showError('Ese grupo ya está cerrado.'); return }
  if (m.includes('codigo_invalido')) { showError('El grupo no existe.'); return }
  showError('No pudimos sumarte al grupo. Probá de nuevo.')
}

// initialErrorMsg: si viene, se pinta como banner persistente arriba (cubre
// el caso post-joinGroup-error donde el alert() era unreliable en mobile;
// fix Bloque 8 cap). null por default — back-compat con tests existentes.
export const renderInvitado = (codigo, preview, onJoin, initialErrorMsg = null) => {
  if (getCurrentUser()) {
    renderInvitadoLogueado(codigo, preview, onJoin, initialErrorMsg)
  } else {
    renderInvitadoSinSesion(codigo, preview, onJoin, initialErrorMsg)
  }
}

// Pinta el mensaje en el error box si vino. Llamado al final de cada
// renderInvitadoXxx (post wireInvitadoSubmit).
const paintInitialError = (msg) => {
  if (!msg) return
  const box = document.getElementById('invitadoError')
  if (!box) return
  box.textContent = msg
  box.classList.add('visible')
}
